import Bugsnag from '@bugsnag/js';
import { ref, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CreateZoomPhoneWidgetEventRequestZoomPhoneWidgetEventEventTypeEnum } from '@/api/openapi';
import { ZoomPhoneResultApiService } from '@/api/user/resources/zoom_phone_result';
import { ZoomPhoneWidgetEventApiService } from '@/api/user/resources/zoom_phone_widget_event';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useToast } from '@/composable/toast';
import { REFRESH_CALL_LIST_KEY, TRACK_VOICE_ANALYSIS_KEY } from '@/composable/va-app/zoom-phone-widget/injection-keys';
import { useZoomPhoneConfirmDialog } from '@/composable/va-app/zoom-phone-widget/zoom-phone-confirm-dialog';
import { ZOOM_PHONE_WIDGET_VISIBLE_KEY } from '@/composable/va-app/zoom-phone-widget/zoom-phone-widget-visible';
import { initZoomPhone, setCustomCallRingingHandler, setCustomCallConnectedHandler, setCustomCallEndedHandler } from '@/services/zoom_phone_service';
import type { TCallRingingEventData, TCallConnectedEventData, TCallEndedEventData, TCallerCallee } from '@/services/zoom_phone_types';

interface ConnectedCallInfo {
  callId: string;
  startedAt: string;
  endedAt?: string;
  caller: TCallerCallee;
  callee: TCallerCallee;
}

export const useZoomPhoneForVa = () => {
  const { t } = useI18n();
  const { errorToast, successToast } = useToast();
  const { inject: injectCurrentUser } = useCurrentUserProvider();
  const { currentUser } = injectCurrentUser();
  const { showConfirmDialog: showDialog } = useZoomPhoneConfirmDialog();

  const widgetVisible = inject(ZOOM_PHONE_WIDGET_VISIBLE_KEY, undefined);
  const refreshCallList = inject(REFRESH_CALL_LIST_KEY, undefined);
  const trackVoiceAnalysis = inject(TRACK_VOICE_ANALYSIS_KEY, undefined);

  // 接続済みコール情報を保存（callId -> ConnectedCallInfo）
  const connectedCalls = ref<Map<string, ConnectedCallInfo>>(new Map());

  const isDialogShowing = ref(false);

  /**
   * 確認ダイアログを表示
   * 既にダイアログ表示中の場合は後から来たイベントを破棄する
   */
  const showConfirmDialog = async (callInfo: ConnectedCallInfo) => {
    if (isDialogShowing.value) {
      return;
    }

    isDialogShowing.value = true;

    try {
      const clientName = currentUser.value?.clientName ?? '不明';
      const phoneNumber = callInfo.callee.phoneNumber || '不明';

      const ok = await showDialog({
        clientName,
        startedAt: callInfo.startedAt,
        endedAt: callInfo.endedAt,
        phoneNumber,
      });

      if (ok) {
        // NOTE: 同一通話の保存ダイアログを複数タブで開いている場合、後から保存した
        //       タブでは400（重複エラー）が返る。この場合は専用メッセージのみ表示し、
        //       汎用の保存失敗トーストは重ねて出さない。
        let isDuplicateError = false;

        try {
          const api = new ZoomPhoneResultApiService();
          const response = await api.createZoomPhoneResult({
            request: {
              createZoomPhoneResultRequest: {
                zoomPhoneResult: {
                  callId: callInfo.callId,
                  callerDidNumber: callInfo.caller.phoneNumber,
                  calleeDidNumber: callInfo.callee.phoneNumber,
                  startedAt: callInfo.startedAt,
                  endedAt: callInfo.endedAt ?? new Date().toISOString(),
                },
              },
            },
            errorHandlers: {
              400: (error: { response?: { data?: { errorMessage?: string } } }) => {
                isDuplicateError = true;
                errorToast(error?.response?.data?.errorMessage ?? t('vaApp.zoomPhone.error.saveFailed'));
              },
            },
          });

          // レスポンスから ID と calleeDidNumber を取得
          const savedResult = response.data.zoomPhoneResult;
          const savedId = savedResult.id;
          const savedCalleeDidNumber = savedResult.calleeDidNumber || undefined;

          if (trackVoiceAnalysis?.value && savedId) {
            trackVoiceAnalysis.value(savedId, savedCalleeDidNumber);
          }
          successToast(t('vaApp.zoomPhone.saveSuccess'));

          // 一覧更新の失敗は無視する
          if (refreshCallList?.value) {
            try {
              await refreshCallList.value();
            } catch {
              // NOTE: 一覧の更新失敗時は何もしない
            }
          }
        } catch {
          if (!isDuplicateError) {
            errorToast(t('vaApp.zoomPhone.error.saveFailed'));
          }
        }
      }
    } finally {
      isDialogShowing.value = false;
    }
  };

  /**
   * 発信イベントを保存する
   * NOTE: モニタリング用途のため、保存失敗は通話中・通話後のフローに影響させない
   */
  const saveWidgetEvent = async (callId: string, eventType: CreateZoomPhoneWidgetEventRequestZoomPhoneWidgetEventEventTypeEnum): Promise<void> => {
    const api = new ZoomPhoneWidgetEventApiService();
    try {
      await api.createZoomPhoneWidgetEvent({
        request: {
          createZoomPhoneWidgetEventRequest: {
            zoomPhoneWidgetEvent: {
              callId,
              eventType,
            },
          },
        },
      });
    } catch (error) {
      // NOTE: 保存失敗はフローに影響させず、モニタリングデータの欠損として Bugsnag にのみ通知する
      if (Bugsnag.isStarted()) {
        Bugsnag.notify(error as Error);
      }
    }
  };

  /**
   * zp-call-ringing-event ハンドラ
   * 着信・発信が開始されたときに ZoomPhoneWidget を開く
   */
  const handleCallRinging = (_data: TCallRingingEventData): void => {
    if (widgetVisible) {
      widgetVisible.value = true;
    }
  };

  /**
   * zp-call-connected-event ハンドラ
   * 通話が接続されたときに呼び出される
   */
  const handleCallConnected = (data: TCallConnectedEventData): void => {
    const { callId, dateTime, caller, callee } = data;

    // 通話接続イベントを保存する(ended との組で確認ダイアログの表示可否を判定するため)
    void saveWidgetEvent(callId, 'connected');

    connectedCalls.value.set(callId, {
      callId,
      startedAt: dateTime,
      caller,
      callee,
    });
  };

  /**
   * zp-call-ended-event ハンドラ
   * 通話が終了したときに呼び出される
   */
  const handleCallEnded = (data: TCallEndedEventData): void => {
    const { callId, dateTime: endedAt, result } = data;

    // 通話終了イベントを保存する(ダイアログの表示可否に関わらず記録する)
    if (result === 'ended') {
      void saveWidgetEvent(callId, 'ended');
    }

    const callInfo = connectedCalls.value.get(callId);

    if (!callInfo) return;

    // missed/rejectedの場合はダイアログを表示せずにクリーンアップのみ
    connectedCalls.value.delete(callId);

    if (result !== 'ended') return;

    callInfo.endedAt = endedAt;
    void showConfirmDialog(callInfo);
  };

  /**
   * Zoom Phone初期化とカスタムハンドラを登録
   */
  const registerHandlers = (): void => {
    // Zoomイベントリスナーを初期化
    initZoomPhone();

    // VAアプリ専用のカスタムハンドラを登録
    setCustomCallRingingHandler(handleCallRinging);
    setCustomCallConnectedHandler(handleCallConnected);
    setCustomCallEndedHandler(handleCallEnded);
  };

  /**
   * カスタムハンドラをクリーンアップ
   */
  const unregisterHandlers = (): void => {
    setCustomCallRingingHandler(null);
    setCustomCallConnectedHandler(null);
    setCustomCallEndedHandler(null);

    connectedCalls.value.clear();
    isDialogShowing.value = false;
  };

  return {
    registerHandlers,
    unregisterHandlers,
  };
};
